import { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

let inFlightSummaryRequest = null;

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const fetchLegacyDashboardSummary = async () => {
  const [totalsResponse, customersResponse] = await Promise.all([
    axiosInstance('/transaction/total_transaction_value', 'get'),
    axiosInstance('/customer/get_only_customers', 'get', {
      pageNum: '',
      pageSize: '',
      keyword: '',
    }),
  ]);

  if (!totalsResponse || totalsResponse.status >= 400) {
    throw new Error(totalsResponse?.data?.message || 'Unable to load dashboard data');
  }

  if (!customersResponse || customersResponse.status >= 400) {
    throw new Error(customersResponse?.data?.message || 'Unable to load dashboard data');
  }

  const totals = totalsResponse.data || {};
  const purchases = toNumber(totals.TotalPurchases);
  const payments = toNumber(totals.TotalPayments);
  const returns = toNumber(totals.TotalReturns);

  return {
    totals: {
      purchases,
      payments,
      returns,
      balance: purchases - payments - returns,
    },
    customers: customersResponse.data?.data || [],
  };
};

const fetchDashboardSummary = () => {
  if (!inFlightSummaryRequest) {
    inFlightSummaryRequest = axiosInstance('/dashboard/summary', 'get')
      .then((response) => {
        if (response?.status === 404) return fetchLegacyDashboardSummary();
        if (!response || response.status >= 400) {
          throw new Error(response?.data?.message || 'Unable to load dashboard data');
        }
        return response.data;
      })
      .finally(() => {
        inFlightSummaryRequest = null;
      });
  }

  return inFlightSummaryRequest;
};

export const useDashboardSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');

    try {
      const response = await fetchDashboardSummary();
      if (requestIdRef.current === requestId) setSummary(response);
    } catch (requestError) {
      if (requestIdRef.current === requestId) setError(requestError.message);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();

    return () => {
      requestIdRef.current += 1;
    };
  }, [load]);

  return { summary, loading, error, retry: load };
};

export const __resetDashboardSummaryRequestForTests = () => {
  inFlightSummaryRequest = null;
};
